import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowRight as ArrowRightIcon,
  ArrowDown as ArrowDownIcon,
  Category as CategoryIcon,
} from "@/components/icons";
import type { FilterField, TableColumn } from "@/types/component";
import type { Category } from "../types/categories.types";
import type { CreateCategoryPayload } from "../types/categories.types";
import { categoriesApi } from "../services/categories.api";
import CategoryModal from "../components/CategoryModal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./CategoriesPage.module.scss";

type TreeRow = Category & {
  depth: number;
  isEmptyHint?: boolean;
  hintParentId?: string;
};

const CategoriesPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.CATEGORIES_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.CATEGORIES_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.CATEGORIES_DELETE);

  const [rootRows, setRootRows] = useState<Category[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [flatResults, setFlatResults] = useState<Category[]>([]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [childrenMap, setChildrenMap] = useState<Record<string, Category[]>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [rootCategoriesForModal, setRootCategoriesForModal] = useState<
    Category[]
  >([]);
  const [parentSearchTerm, setParentSearchTerm] = useState("");
  const [debouncedParentSearch, setDebouncedParentSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Sunucu kaynaklı (409) mükerrer isim hataları — modal içinde inline gösterilir.
  const [addSubmitError, setAddSubmitError] = useState<string | null>(null);
  const [editSubmitError, setEditSubmitError] = useState<string | null>(null);

  const isSearchMode = debouncedSearchTerm.trim() !== "";

  const getIsActive = () => {
    const raw = filterValues.isActive as string | undefined;
    if (raw === "true") return true;
    if (raw === "false") return false;
    return undefined;
  };

  const fetchRoots = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await categoriesApi.getList({
        page: currentPage,
        limit: pageSize,
        isActive: getIsActive(),
        parentId: null,
      });
      setRootRows(res.data);
      setTotalRecords(res.total);
      setChildrenMap({});
      setExpandedIds(new Set());
    } catch {
      setRootRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filterValues]);

  const fetchFlatSearch = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await categoriesApi.getList({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm.trim(),
        isActive: getIsActive(),
      });
      setFlatResults(res.data);
      setTotalRecords(res.total);
    } catch {
      setFlatResults([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchTerm, filterValues]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isSearchMode) fetchFlatSearch();
    else fetchRoots();
  }, [isSearchMode, fetchRoots, fetchFlatSearch]);

  // Üst kategori adayları: TÜM kategoriler (yalnız kökler değil). Kategori ağacı
  // çok seviyeli olabilmeli — alt kategorinin altına da kategori eklenebilsin.
  // Kendisi ve kendi alt ağacı CategoryModal içinde eleniyor (döngü önleme).
  const fetchRootCategoriesForModal = useCallback(async (search: string) => {
    try {
      const res = await categoriesApi.getList({
        limit: 200,
        search: search.trim() || undefined,
      });
      setRootCategoriesForModal(res.data ?? []);
    } catch {
      setRootCategoriesForModal([]);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen || isEditModalOpen) {
      setParentSearchTerm("");
      setDebouncedParentSearch("");
    }
  }, [isModalOpen, isEditModalOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedParentSearch(parentSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [parentSearchTerm]);

  useEffect(() => {
    if (isModalOpen || isEditModalOpen)
      fetchRootCategoriesForModal(debouncedParentSearch);
  }, [
    isModalOpen,
    isEditModalOpen,
    debouncedParentSearch,
    fetchRootCategoriesForModal,
  ]);

  const toggleExpand = async (id: string) => {
    if (expandedIds.has(id)) {
      setExpandedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      return;
    }
    setExpandedIds((prev) => new Set(prev).add(id));
    if (!childrenMap[id]) {
      setLoadingIds((prev) => new Set(prev).add(id));
      try {
        const res = await categoriesApi.getList({
          parentId: id,
          limit: 100,
          isActive: getIsActive(),
        });
        setChildrenMap((prev) => ({ ...prev, [id]: res.data }));
      } catch {
        setChildrenMap((prev) => ({ ...prev, [id]: [] }));
      } finally {
        setLoadingIds((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      }
    }
  };

  const visibleRows: TreeRow[] = useMemo(() => {
    if (isSearchMode) {
      return flatResults.map((c) => ({ ...c, depth: 0 }));
    }
    const out: TreeRow[] = [];
    const walk = (cat: Category, depth: number) => {
      out.push({ ...cat, depth });
      if (expandedIds.has(cat.id)) {
        const kids = childrenMap[cat.id];
        if (kids && kids.length > 0) {
          kids.forEach((k) => walk(k, depth + 1));
        } else if (kids && kids.length === 0 && !loadingIds.has(cat.id)) {
          out.push({
            id: `${cat.id}__empty`,
            name: "",
            parentId: cat.id,
            depth: depth + 1,
            isEmptyHint: true,
            hintParentId: cat.id,
          });
        }
      }
    };
    rootRows.forEach((r) => walk(r, 0));
    return out;
  }, [rootRows, expandedIds, childrenMap, loadingIds, flatResults, isSearchMode]);

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const refreshAfterMutation = () => {
    if (isSearchMode) fetchFlatSearch();
    else fetchRoots();
  };

  /**
   * Sunucu hatasını (status + mesaj) axios error'undan çıkarır.
   * 409 = aynı üst kategori altında mükerrer isim → modal açık kalır ve mesaj
   * Kategori Adı alanının altında inline gösterilir. Diğer hatalar toast olur
   * (create/update isteklerinde interceptor toast'ı `skipErrorToast` ile kapalı).
   */
  const extractApiError = (error: unknown) => {
    const err = error as {
      response?: {
        status?: number;
        data?: { message?: string; error?: string };
      };
    };
    return {
      status: err?.response?.status,
      message: err?.response?.data?.message ?? err?.response?.data?.error,
    };
  };

  const handleMutationError = (
    error: unknown,
    setInlineError: (message: string) => void,
  ) => {
    const { status, message } = extractApiError(error);
    if (status === 409) {
      setInlineError(message ?? t("categories.duplicateName"));
      return;
    }
    toast.error(message ?? t("apiClient.unexpectedError"));
  };

  const handleAddCategory = async (payload: CreateCategoryPayload) => {
    setAddSubmitError(null);
    try {
      await categoriesApi.create(payload);
      toast.success(t("categories.createSuccess"));
      setIsModalOpen(false);
      refreshAfterMutation();
    } catch (error) {
      handleMutationError(error, setAddSubmitError);
    }
  };

  const handleEditClick = (row: Category) => {
    setEditTarget(row);
    setEditSubmitError(null);
    setIsEditModalOpen(true);
    handleDropdownToggle(row.id, false);
  };

  const handleUpdateCategory = async (payload: CreateCategoryPayload) => {
    if (!editTarget) return;
    setEditSubmitError(null);
    try {
      await categoriesApi.update(editTarget.id, payload);
      toast.success(t("categories.updateSuccess"));
      setEditTarget(null);
      setIsEditModalOpen(false);
      refreshAfterMutation();
    } catch (error) {
      handleMutationError(error, setEditSubmitError);
    }
  };

  const handleDeleteClick = (row: Category) => {
    setDeleteTarget(row);
    handleDropdownToggle(row.id, false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await categoriesApi.delete(deleteTarget.id);
      toast.success(t("categories.deleteSuccess"));
      setDeleteTarget(null);
      refreshAfterMutation();
    } catch {
      /* toast handled by interceptor */
    } finally {
      setIsDeleting(false);
    }
  };

  const filterFields: FilterField[] = [
    {
      name: "isActive",
      label: "Aktiflik",
      type: "dropdown",
      placeholder: t("categoriesPage.selectPlaceholder"),
      options: [
        { label: t("categoriesPage.all"), value: "" },
        { label: "Aktif", value: "true" },
        { label: "Pasif", value: "false" },
      ],
    },
  ];

  const renderNameCell = (row: TreeRow) => {
    if (row.isEmptyHint) {
      return (
        <div
          className={styles.treeCell}
          style={{ paddingLeft: row.depth * 24 }}
        >
          <span className={styles.emptyHint}>
            {t("categories.noChildren")}
          </span>
        </div>
      );
    }

    const isExpanded = expandedIds.has(row.id);
    const isLoadingChildren = loadingIds.has(row.id);
    const isRoot = !row.parentId;
    const hasNoChildren =
      typeof row.childCount === "number" && row.childCount === 0;
    const canExpand = !isSearchMode && !hasNoChildren;

    return (
      <div
        className={styles.treeCell}
        style={{ paddingLeft: row.depth * 24 }}
      >
        {row.depth > 0 && <span className={styles.treeGuide} aria-hidden />}
        {canExpand ? (
          <button
            type="button"
            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(row.id);
            }}
            aria-label={isExpanded ? t("categoriesPage.collapse") : t("categoriesPage.expand")}
          >
            {isLoadingChildren ? (
              <span className={styles.spinner} aria-hidden />
            ) : isExpanded ? (
              <ArrowDownIcon width={14} height={14} />
            ) : (
              <ArrowRightIcon width={14} height={14} />
            )}
          </button>
        ) : (
          <span className={styles.chevronPlaceholder} />
        )}
        <span
          className={`${styles.iconWrap} ${isRoot ? styles.iconRoot : styles.iconLeaf}`}
        >
          <CategoryIcon width={16} height={16} />
        </span>
        <span
          className={isRoot ? styles.rootCategoryName : styles.subCategoryName}
        >
          {row.name}
        </span>
        {typeof row.childCount === "number" && row.childCount > 0 && (
          <span
            className={styles.childCountBadge}
            title={`${row.childCount} alt kategori`}
          >
            {row.childCount}
          </span>
        )}
        {isSearchMode && row.parentName && (
          <span className={styles.breadcrumb}>in {row.parentName}</span>
        )}
      </div>
    );
  };

  const columns: TableColumn<TreeRow>[] = [
    {
      header: t("categoriesPage.headerCategory"),
      accessor: renderNameCell,
      className: styles.nameColumn,
    },
    {
      header: t("categoriesPage.headerDescription"),
      accessor: (row) =>
        row.isEmptyHint ? (
          ""
        ) : (
          <span className={styles.descriptionCell} title={row.description ?? ""}>
            {row.description ?? "—"}
          </span>
        ),
      className: styles.descriptionColumn,
    },
    {
      header: "DURUM",
      accessor: (row) =>
        row.isEmptyHint ? (
          ""
        ) : (
          <span
            className={`${styles.statusBadge} ${row.isActive ? styles.statusActive : styles.statusPassive}`}
          >
            {row.isActive ? "Aktif" : "Pasif"}
          </span>
        ),
      className: styles.statusColumn,
    },
  ];

  const renderActions = (row: TreeRow) => {
    if (row.isEmptyHint) return null;
    // Hiç bir aksiyon yetkisi yoksa butonu hiç gösterme.
    if (!canUpdate && !canDelete) return null;
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;
    const menuContent = (
      <div className={styles.actionMenu}>
        {canUpdate && (
          <button
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
          >
            <EditIcon width={20} height={20} />
            {t("buttons.edit")}
          </button>
        )}
        {canDelete && (
          <button
            className={`${styles.actionMenuItem} ${styles.error}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
          >
            <DeleteIcon width={20} height={20} />
            {t("buttons.delete")}
          </button>
        )}
      </div>
    );
    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("categories.title")} />
      <PageHeader
        searchPlaceholder="Kategori ara..."
        searchValue={searchTerm}
        onSearch={handleSearch}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={
          canCreate
            ? () => {
                setAddSubmitError(null);
                setIsModalOpen(true);
              }
            : undefined
        }
        addButtonLabel={canCreate ? "Kategori Ekle" : undefined}
      />
      <Table
        columns={columns}
        data={visibleRows}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("categoriesPage.empty")}
      />
      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setAddSubmitError(null);
        }}
        onSubmit={handleAddCategory}
        rootCategories={rootCategoriesForModal}
        onSearchParent={setParentSearchTerm}
        submitError={addSubmitError}
        onClearSubmitError={() => setAddSubmitError(null)}
      />
      <CategoryModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditTarget(null);
          setEditSubmitError(null);
        }}
        onSubmit={handleUpdateCategory}
        rootCategories={rootCategoriesForModal}
        onSearchParent={setParentSearchTerm}
        editCategory={editTarget}
        title={t("categories.editTitle")}
        submitError={editSubmitError}
        onClearSubmitError={() => setEditSubmitError(null)}
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t("categories.deleteTitle")}
        message={
          deleteTarget
            ? t("categories.deleteConfirmMessage", { name: deleteTarget.name })
            : ""
        }
        confirmText={t("categories.deleteConfirm")}
        loading={isDeleting}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default CategoriesPage;
